import React, { useEffect, useState } from "react";
import { CalendarPlus, CalendarCheck, EnvelopeSimple, BellRinging, Trash, Clock } from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../i18n";
import api from "../../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { formatMAD } from "../../lib/format";

const TYPE_LABEL = { any: "Tout type", new_build: "Programme neuf", renovation: "Rénovation", logistics: "Entrepôt", business_premises: "Local pro" };

const ServiceDashboard = () => {
  const { user } = useAuth();
  const { t } = useT();
  const [slots, setSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [start, setStart] = useState("");

  const load = () => {
    api.get("/slots").then(({ data }) => setSlots(data)).catch(() => {});
    api.get("/appointments").then(({ data }) => setAppointments(data)).catch(() => {});
    api.get("/contact").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addSlot = async () => {
    if (!start) return;
    const s = new Date(start);
    const e = new Date(s.getTime() + 45 * 60000);
    await api.post("/slots", { start: s.toISOString(), end: e.toISOString() });
    toast.success(t("svc.slot_added"));
    setStart("");
    load();
  };
  const delSlot = async (id) => { await api.delete(`/slots/${id}`); load(); };
  const handleLead = async (id) => { await api.put(`/contact/${id}/handled`); load(); };

  return (
    <div className="relative z-10 pt-[72px] min-h-screen bg-background">
      <div className="bg-navy text-white py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <span className="overline text-gold">{t("svc.role")} · {user?.name}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tight mt-2">{t("svc.title")}</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <Tabs defaultValue="appointments">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="appointments"><CalendarCheck size={16} className="me-2" /> {t("svc.tab_rdv")} ({appointments.length})</TabsTrigger>
            <TabsTrigger value="slots"><CalendarPlus size={16} className="me-2" /> {t("svc.tab_slots")}</TabsTrigger>
            <TabsTrigger value="leads"><EnvelopeSimple size={16} className="me-2" /> {t("svc.tab_leads")} ({leads.length})</TabsTrigger>
            <TabsTrigger value="alerts"><BellRinging size={16} className="me-2" /> {t("svc.tab_expect")} ({alerts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="mt-8 space-y-3">
            {appointments.length === 0 && <Empty>{t("svc.no_rdv")}</Empty>}
            {appointments.map((a) => (
              <div key={a.id} className="bg-white border border-navy/10 rounded-xl p-4 flex items-center gap-4">
                <CalendarCheck size={26} weight="light" className="text-gold" />
                <div className="flex-1">
                  <div className="font-medium text-navy">{new Date(a.start).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="text-xs text-muted-foreground">{a.user_name} · {a.user_email} · sujet : {a.subject}</div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="slots" className="mt-8">
            <div className="bg-white border border-navy/10 rounded-2xl p-6 mb-6 flex flex-col sm:flex-row items-end gap-3">
              <div className="flex-1 w-full">
                <label className="text-sm font-medium text-navy block mb-2">{t("svc.add_slot")}</label>
                <Input data-testid="slot-datetime" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <button data-testid="slot-add" onClick={addSlot} className="bg-navy text-white font-semibold px-6 py-2.5 rounded-full hover:bg-[#254B75] transition-colors">{t("svc.add")}</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {slots.map((s) => (
                <div key={s.id} className="bg-white border border-navy/10 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-navy">{new Date(s.start).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> {new Date(s.start).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <button onClick={() => delSlot(s.id)} className="text-muted-foreground hover:text-red-500"><Trash size={18} /></button>
                </div>
              ))}
              {slots.length === 0 && <Empty>{t("svc.no_slots")}</Empty>}
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-8 space-y-3">
            {leads.length === 0 && <Empty>{t("svc.no_leads")}</Empty>}
            {leads.map((l) => (
              <div key={l.id} className="bg-white border border-navy/10 rounded-xl p-4">
                <div className="flex justify-between gap-3 items-start">
                  <div>
                    <div className="font-medium text-navy">{l.name} {l.handled && <span className="text-xs text-green-600 ms-2">✓ traité</span>} {l.subject === "assistant" && <span className="text-xs text-gold ms-2">via José</span>}</div>
                    <div className="text-xs text-muted-foreground">{l.email} · {l.phone}</div>
                  </div>
                  {!l.handled && <button onClick={() => handleLead(l.id)} className="text-xs text-gold hover:underline">Marquer traité</button>}
                </div>
                <p className="text-sm text-muted-foreground mt-2">{l.message}</p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="alerts" className="mt-8 space-y-3">
            {alerts.length === 0 && <Empty>{t("svc.no_expect")}</Empty>}
            {alerts.map((a) => (
              <div key={a.id} className="bg-white border border-navy/10 rounded-xl p-4 flex items-center gap-4">
                <BellRinging size={22} weight="light" className="text-gold" />
                <div className="flex-1">
                  <div className="font-medium text-navy">{TYPE_LABEL[a.type] || a.type} · {a.city !== "any" ? a.city : "toutes villes"}</div>
                  <div className="text-xs text-muted-foreground">{a.user_name || "Prospect"} {a.user_email ? `· ${a.user_email}` : ""} {a.max_price ? `· ≤ ${formatMAD(a.max_price)} MAD` : ""} {a.note ? `· ${a.note}` : ""} {a.source === "assistant" && "· via José"}</div>
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const Empty = ({ children }) => <div className="bg-white border border-dashed border-navy/20 rounded-2xl p-10 text-center text-muted-foreground col-span-full">{children}</div>;

export default ServiceDashboard;
